import { type Options } from './modules/ComponentsLibrary/Field';

export const MEALS_RATE = 59;

export const MAX_PAGES = 20;
export const WaiverTypes = [
  { label: 'Final Waiver', value: 2 },
  { label: 'Partial Waiver', value: 3 },
  { label: 'Waiver On File', value: 4 },
  { label: 'No Waiver Required', value: 5 },
];
export const APP_URL = 'https://app.kalosflorida.com/';
export const BASE_URL = `${APP_URL}index.cfm`;

export const OPTION_BLANK = '-- Select --';
export const OPTION_ALL = '-- All --';

export const NOTE_REPORT_LIMIT = 100;

export const LOG_IMAGE_BUCKET = 'project-log-images';

export const CREDIT_CARD_ACCOUNTS = ['Capital One (1974)', 'Capital One (0922)'];
export const CALLBACK_DISPOSITION = [
  { label: 'At-Fault Callback', value: 1 },
  { label: 'Unreasonable Expectations', value: 2 },
  { label: 'Technician Miscommunication', value: 3 },
  { label: 'Not-At-Fault Callback', value: 4 },
];
export const PROP_LEVEL = 'Used for property-level billing only';
export const INTERNAL_DOCUMENTS_BUCKET = 'kalos-internal-docs';

export const JOB_STATUS_LIST = [
  'Requested',
  'Confirmed',
  'Enroute',
  'On Call',
  'Delayed',
  'Incomplete',
  'Part on order',
  'Quote Requested',
  'Pending Approval',
  'Pend Sched',
  'Canceled',
  'Completed',
];

export const EVENT_STATUS_LIST = [...JOB_STATUS_LIST, 'Admin Review', 'Archived'];

export const PENDING_JOB_STATUSES = [
  { status: 'Pending Approval', label: 'Pending Approval' },
  { status: 'Pend Sched', label: 'Pending Schedule' },
  { status: 'Part on order', label: 'Part On Order' },
] as const;

export const ALL_PENDING_JOB_STATUSES: string[] = PENDING_JOB_STATUSES.map(({ status }) => status);

// log_jobStatus is free text; casing varies ('Part on Order' also occurs).
export const resolvePendingJobStatus = (status?: string): string | undefined =>
  PENDING_JOB_STATUSES.find(
    ({ status: pending }) => pending.toLowerCase() === status?.trim().toLowerCase(),
  )?.status;

// Bucket status lists mirror GetJobStatusBreakdown in kalos-core
// (server/analytics/job_status/calculator.go); keep the two in sync.
export const JOB_STATUS_BUCKETS = {
  open: {
    label: 'Intake / Scheduling',
    shortLabel: 'Intake',
    statuses: ['Requested', 'Pend Sched', 'Confirmed', 'Proposed', 'Locked In'],
  },
  active: {
    label: 'Active',
    shortLabel: 'Active',
    statuses: ['Enroute', 'On Call', 'Admin Review', 'In Progress'],
  },
  blocked: {
    label: 'Blocked / Stalled',
    shortLabel: 'Blocked',
    statuses: ['Delayed', 'Part on order', 'Incomplete', 'Quote Requested'],
  },
  completed: {
    label: 'Completed / Closed',
    shortLabel: 'Completed',
    statuses: ['Completed', 'Canceled', 'Declined'],
  },
} as const;

export type JobStatusBucketKey = keyof typeof JOB_STATUS_BUCKETS;

// Project lifecycle order: Proposed -> Locked In -> In Progress -> Completed.
// 'Active' and 'Confirmed' are retired for projects (CU-86batet2w); migration 326
// in kalos-core converts existing rows, but pre-migration data may still carry them.
export const PROJECT_STATUS_LIST = [
  'Proposed',
  'Locked In',
  'In Progress',
  'Completed',
  'On Hold',
  'Canceled',
  'Archived',
];

export const PROJECT_STATUS_DESCRIPTIONS: { [key: string]: string } = {
  Proposed: 'Soft — client not yet committed; follow up until the date is confirmed.',
  'Locked In': 'Firm — date confirmed, on the schedule board, crews assigned.',
  'In Progress': 'Work underway — time and billing accruing.',
  Completed: 'Work finished — close-out.',
  'On Hold': 'Paused — kept out of active work until resumed.',
  Canceled: 'Will not proceed.',
  Archived: 'Hidden from active views.',
};

// Schedule Confidence (CU-86batet2w slice 2): the schedule-commitment signal,
// decoupled from lifecycle stage. Gates dispatch-board visibility and time
// logging. Defaults to match status but is manually overridable.
export const SCHEDULE_CONFIDENCE_LIST = ['Soft', 'Firm', 'Active'];

export const SCHEDULE_CONFIDENCE_DESCRIPTIONS: { [key: string]: string } = {
  Soft: 'Client not committed — kept off the dispatch board; requires a follow-up owner + date.',
  Firm: 'Date confirmed — placed on the schedule board.',
  Active: 'Work underway — time logging allowed.',
};

// Confidence values that put a project on the dispatch board (Soft stays off).
export const DISPATCH_VISIBLE_CONFIDENCE = ['Firm', 'Active'];

// Default schedule confidence for a project lifecycle status — mirrors the
// backend defaultScheduleConfidenceForStatus so the UI shows the same default
// for rows created before the field was populated.
export const getDefaultScheduleConfidence = (status: string | undefined): string => {
  switch (status) {
    case 'Locked In':
      return 'Firm';
    case 'In Progress':
      return 'Active';
    default:
      return 'Soft';
  }
};

export const BILLING_STATUS_TYPE_LIST = [
  'Pending',
  'Billed',
  'Canceled',
  'Paid',
  'Charity',
  'Collected',
];

export const NOTIFICATIONS_STATUS_TYPE_LIST = ['Deletions only'];

export const SPIFF_KIND_TYPE_LIST = ['Monthly', 'Weekly'];

export const FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME = 350;
export const FORM_SEARCH_PARAMS_SYNC_DEBOUNCE_TIME = 650;

// Invoice Net terms stored in contract.paymentTerms; shared by the memberships drawer + commercial wizard.
export const PAYMENT_TERMS = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on receipt'] as const;

export const PAYMENT_TYPE_LIST = [
  'Pre-Paid',
  'Cash',
  'Check',
  'Credit Card',
  'Billing',
  'Financing',
  'Warranty',
  'Manufacturer Warranty',
  'No Charge',
  'Internal Revenue Transfer',
  'ACH / Wire',
  'Quote',
  'Charity',
] as const;
export const PAYMENT_TYPE_LIST_OPTIONS: Options = [...PAYMENT_TYPE_LIST];

// Retired/renamed types still stored on legacy rows; matching/filtering/validation must accept them.
export const LEGACY_PAYMENT_TYPES = [
  'Stripe',
  'Helcim',
  'Extended Warranty',
  'AOR Warranty',
  'Service Warranty',
  'Project Warranty',
  'Account Transfer',
] as const;

// Dropdowns editing an existing record append its stored legacy value, else the trigger renders blank.
export const paymentTypeOptionsFor = (current?: string | null): string[] =>
  current && !(PAYMENT_TYPE_LIST as readonly string[]).includes(current)
    ? [...PAYMENT_TYPE_LIST, current]
    : [...PAYMENT_TYPE_LIST];

export const SIGNATURE_PAYMENT_TYPE_LIST: Options = [
  'Credit Card',
  'Cash',
  'Check',
  'Stripe',
  'Helcim',
];

export const PAYMENT_COLLECTED_LIST: Options = [
  'Credit Card',
  'Financing (Complete)',
  'Cash',
  'Check',
  'Stripe',
  'ACH Transfer',
  'Pre-Paid',
  'Charity',
];

export const PAYMENT_STATUS_LIST = [
  'Paid',
  'Pending',
  'Billed',
  'Canceled',
  'Collected',
  // Prepaid PM jobs (the scheduler reads it as covered) and contracts paid up front.
  'Paid on contract',
] as const;
export const PAYMENT_STATUS_LIST_OPTIONS: Options = [...PAYMENT_STATUS_LIST];
export const PROJECT_INVOICE_PAYMENT_STATUS_LIST_OPTIONS: Options = [
  ...PAYMENT_STATUS_LIST,
  'Draft',
  'Sent',
];

/** Payment status for work already covered by a contract's upfront payment. */
export const PAID_ON_CONTRACT_STATUS = 'Paid on contract';

/** Contract payment statuses the backend treats as settled (core `settledPaymentStatuses`). */
export const CONTRACT_SETTLED_PAYMENT_STATUSES: readonly string[] = [
  'Paid',
  'Collected',
  PAID_ON_CONTRACT_STATUS,
];

export const PAYMENT_NOT_COLLECTED_LIST: Options = [
  'Billing',
  'Credit Card',
  'Financing (Complete)',
  'Warranty',
  'AOR Warranty',
  'Stripe',
  'Service Warranty',
  'No Charge',
  'Pre-Paid',
  'Charity',
];

export const DUMMY_USER = 'test';
export const DUMMY_PWD = 'test';

export const CHART_COLORS = {
  blue: 'rgb(51, 102, 204)',
  red: 'rgb(220, 57, 18)',
  orange: 'rgb(255, 153, 0)',
  green: 'rgb(16, 150, 24)',
  purple: 'rgb(153, 0, 153)',
};

export const COLORS = {
  light1: '#FAFAFC',
  light2: '#F2F2F5',
  light3: '#EBEBF0',
  dark1: '#8F90A6',
  dark2: '#555770',
  dark3: '#28293D',
  primary1: '#FF908A',
  primary2: '#FF453A',
  primary3: '#F20E00',
};

export const SERVICE_STATUSES = {
  NO_STATUS: '',
  ENROUTE: 'Enroute',
  ON_CALL: 'On Call',
  SIGNATURE: 'Signature',
  PAYMENT: 'Payment',
  COMPLETED: 'Completed',
  INCOMPLETE: 'Incomplete',
  ADMIN: 'Admin',
  SIGNED_AS: 'signed as',
};

/**
 * ServicesRendered status for diagnostic notes.
 * Records with this status are excluded from time tracking and billing calculations.
 */
export const DIAGNOSTIC_SR_STATUS = 'Diagnostic';

/**
 * Stored OpenAI prompt used to tidy up a customer-facing notes field in place.
 * Empty until the prompt exists in the OpenAI dashboard — the Clean Up buttons stay
 * hidden while it is, so setting the `pmpt_` id here is all that turns them on.
 */
export const AI_TEXT_CLEANUP_PROMPT_ID = '';

/**
 * Maintenance job type, which call-window scheduling limits are keyed on.
 * `job_type.id` for id comparisons; `job_type.name`, which `event.jobType`
 * carries on the calendar payload, for the places that only have the display
 * string. Prefer the id — the name drifts if the job type is ever renamed.
 */
export const MAINTENANCE_JOB_TYPE_ID = 3;
export const MAINTENANCE_JOB_TYPE_NAME = 'Maintenance';

export const JOB_STATUS_COLORS: { [key: string]: string } = {
  Requested: 'efc281',
  Confirmed: 'fefdb9',
  Enroute: 'ffff00',
  'On Call': '88edb3',
  Delayed: '07ccec',
  Incomplete: 'bfd4ff',
  'Part on order': 'aa93ea',
  'Pend Sched': 'fd9834',
  Proposed: 'ffe4b3',
  'Locked In': 'aad4ff',
  'In Progress': 'd6bcfa',
  Canceled: 'e74c3c',
  Completed: '55e552',
  'Admin Review': 'e1e1e1',
  Archived: '9e9e9e',
  'Pending Approval': 'fd9834',
  'Quote Requested': 'e3a6ff',
};

export const PROJECT_TASK_STATUS_COLORS: { [key: number]: string } = {
  1: '#efc281',
  2: '#88edb3',
  3: '#999',
  4: '#55e552',
  5: '#e1e1e1',
  6: '#07ccec',
  7: '#fefdb9',
  8: '#fd9834',
  9: '#789abc',
};

export const ELEVATION = {
  card: '0px 1px 2px #00000052',
  button: '0px 2px 4px #00000052',
  menu: '0px 4px 8px #00000052',
  card2: '0px 8px 16px #00000052',
  popover: '0px 16px 24px #00000052',
  modals: '0px 20px 32px #00000052',
};

export const ENDPOINT = import.meta.env.VITE_API_URL;

export const QUOTE_PART_AVAILABILITY: { [key: number]: string } = {
  1: 'local',
  2: 'next day',
  3: '3-7 days',
  4: 'more than a week',
  5: 'Truck Stock',
};

export const NEW_HIRE_DRUG_TEST_STATUS: { [key: number]: string } = {
  1: 'Not Started',
  2: 'In Progress',
  3: 'Completed',
  4: 'Failed',
};

export const NEW_HIRE_BACKGROUND_CHECK_STATUS: { [key: number]: string } = {
  1: 'Not Started',
  2: 'In Progress',
  3: 'Completed',
  4: 'Failed',
};

export const NEW_HIRE_STATUS_OPTIONS = [
  { label: 'Not Started', value: 1 },
  { label: 'In Progress', value: 2 },
  { label: 'Completed', value: 3 },
  { label: 'Failed', value: 4 },
];

// Orientation location constants
export const ORIENTATION_LOCATIONS = ['Main Office', 'Warehouse', 'Remote'] as const;
export const ORIENTATION_PHYSICAL_ADDRESSES = [
  '236 Hatteras Ave. Clermont, FL 34711',
  '429 Citrus Tower Blvd, Clermont, FL 34711',
];

export const USA_STATES_OPTIONS: Options = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

// 'Card On File' is no longer typeable — card-on-file state derives from the
// customer's actual authorized cards (86bbdvay3). Legacy rows keep their stored
// value; the selects render it read-only when present.
export const BILLING_TERMS_OPTIONS: Options = ['COD', 'Net 10', 'Net 30', 'Net 45', 'Net 60'];

// Billing terms that represent ongoing invoiced billing (vs point-of-sale payment).
// Billing Instructions only apply to these — see BillingDetailsCard / CustomerEditForm.
export const ONGOING_BILLING_TERMS: string[] = ['Net 10', 'Net 30', 'Net 45', 'Net 60'];

export const PRICING_MODEL_OPTIONS: Options = [
  { label: 'None', value: 0 },
  { label: 'Flat Rate', value: 1 },
  { label: 'Time and Materials', value: 2 },
];

export const WORKERS_COMP_CODES: { label: string; value: number }[] = [
  { label: '5059', value: 5059 },
  { label: '5183', value: 5183 },
  { label: '5190', value: 5190 },
  { label: '5213', value: 5213 },
  { label: '5437', value: 5437 },
  { label: '5537', value: 5537 },
  { label: '5606', value: 5606 },
  { label: '8227', value: 8227 },
  { label: '8742', value: 8742 },
  { label: '6217', value: 6217 },
  { label: '8810', value: 8810 },
];

export const ROWS_PER_PAGE = 25;

export const API_FAILED_GENERAL_ERROR_MSG = 'Error occured while performing this operation.';

export const RESIDENTIAL_OPTIONS: Options = [
  { label: 'Residential', value: 1 },
  { label: 'Commercial', value: 0 },
];

/** String-valued RESIDENTIAL_OPTIONS for SimpleSelect (which requires string values). */
export const RESIDENTIAL_SELECT_OPTIONS = (
  RESIDENTIAL_OPTIONS as { label: string; value: number }[]
).map((o) => ({ value: String(o.value), label: o.label }));

export const CONTACT_METHOD_OPTIONS: Options = ['Phone Call', 'Text', 'Email'];

export const YES_NO_OPTIONS = [
  { label: 'Yes', value: '1' },
  { label: 'No', value: '0' },
];

export const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'] as const;

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const SEARCH_DATE_FORMAT = 'yyyy-MM-dd' as const;

export const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const NULL_TIME = '0001-01-01 00:00:00';
export const EMPTY_VALUES = [0, '', NULL_TIME];
export const PERMISSION_PRIVILEGE = 'privilege';
export const PERMISSION_ROLE = 'role';
export const PERMISSION_DEPARTMENT = 'department';
export const PERMISSION_NAME_MANAGER = 'Manager';
export const PERMISSION_NAME_RECEIPT_REVIEW = 'ReceiptReviewAccess';

export const IRS_SUGGESTED_MILE_FACTOR = 0.7; // $0.56 a mile

export default {
  IRS_SUGGESTED_MILE_FACTOR,
  BASE_URL,
  EVENT_STATUS_LIST,
  DUMMY_PWD,
  DUMMY_USER,
  PAYMENT_TYPE_LIST: PAYMENT_TYPE_LIST_OPTIONS,
  COLORS,
  ENDPOINT,
  ELEVATION,
  USA_STATES_OPTIONS,
  BILLING_TERMS_OPTIONS,
  ROWS_PER_PAGE,
  API_FAILED_GENERAL_ERROR_MSG,
  RESIDENTIAL_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  SERVICE_STATUSES,
  QUOTE_PART_AVAILABILITY,
  SIGNATURE_PAYMENT_TYPE_LIST,
  PAYMENT_COLLECTED_LIST,
  PAYMENT_NOT_COLLECTED_LIST,
  OPTION_BLANK,
  CHART_COLORS,
  MONTHS,
  PERMISSION_PRIVILEGE,
  PERMISSION_ROLE,
  EMPTY_VALUES,
  PERMISSION_DEPARTMENT,
  NEW_HIRE_DRUG_TEST_STATUS,
  NEW_HIRE_BACKGROUND_CHECK_STATUS,
  NEW_HIRE_STATUS_OPTIONS,
  ORIENTATION_LOCATIONS,
  ORIENTATION_PHYSICAL_ADDRESSES,
};
